import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import { useRacehorse360Api } from "hooks/api";
import { horseQueryKeys } from "api/query-keys";
import { DASH } from "common/constants";
import Loader from "components/Loader";
import { useLoggedInUser } from "components/LoggedInUserProvider";
import RiskLevel from "components/RiskLevel";
import RiskLevelSelector from "components/RiskLevelSelector";
import { EVariantExamsHistory } from "components/VetExams/ExamHistoryItem/helper";
import useStyles from "./styles";

interface Props {
  horse: racehorse360.IHorse;
  isLoading: boolean;
}

const HealthRating = (props: Props) => {
  const { horse, isLoading } = props;
  const classes = useStyles();
  const queryClient = useQueryClient();
  const { currentUser } = useLoggedInUser();

  const [riskLevel, setRiskLevel] = useState(horse.healthWatchStatus);
  const [open, setOpen] = useState(false);
  const { useEditHorseHealthWatchStatus } = useRacehorse360Api();
  const { mutateAsync: editHorseHealthWatchStatus, isPending: isUpdating } =
    useEditHorseHealthWatchStatus();

  const handleRiskButtonClick = () => {
    setOpen(true);
  };

  const handleChange = (newRiskLevel: racehorse360.HealthWatchStatus) => {
    setRiskLevel(newRiskLevel);
  };

  const handleClose = () => {
    setOpen(false);
    setRiskLevel(horse.healthWatchStatus);
  };

  const handleConfirm = async () => {
    setOpen(false);
    await editHorseHealthWatchStatus({
      horseId: horse.id,
      healthWatchStatus: riskLevel
    });

    await queryClient.invalidateQueries({
      queryKey: [horseQueryKeys.detail(horse.id)]
    });
  };

  const renderRiskLevel = () => {
    if (!horse.healthWatchStatus) {
      if (currentUser.isRegulatoryVet) {
        return DASH;
      }

      return (
        <Button
          className={classes.healthRatingButton}
          onClick={handleRiskButtonClick}
        >
          +
        </Button>
      );
    }

    return (
      <RiskLevel
        status={horse.healthWatchStatus}
        onClick={handleRiskButtonClick}
        variant={EVariantExamsHistory.DETAILS}
      />
    );
  };

  if (!horse) {
    return <></>;
  }

  return (
    <>
      <Box className={classes.healthRating}>
        {(isLoading || isUpdating) && <Loader size={16} />}
        {renderRiskLevel()}
      </Box>
      {open && (
        <Dialog
          className={classes.dialog}
          classes={{ paper: classes.dialogPaper }}
          open={open}
          onClose={handleClose}
        >
          <DialogTitle className={classes.dialogTitle}>
            <Box>{horse.name}</Box>
            <Box className={classes.dialogTitleDescription}>
              Select and confirm Risk Level for this horse below.
            </Box>
          </DialogTitle>

          <DialogContent className={classes.dialogContent}>
            <RiskLevelSelector riskLevel={riskLevel} onChange={handleChange} />
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button className={classes.confirmButton} onClick={handleConfirm}>
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default HealthRating;
